import {
  types as t,
  flow,
  getParent,
  getEnv,
  getSnapshot,
  onSnapshot,
  type Instance,
  type IDisposer,
} from 'mobx-state-tree';
import { DateTime } from 'luxon';
import Model, { type IModel } from '../model';
import type { IModelInformation } from './model_information';
import type { IProject } from './project';
import type { IStoreEnv } from '../env';
import { throttle } from 'lodash';
import { TimeTraveller } from 'mst-middlewares';
import { exportEDGImage, exportFSMImage } from '../../diagram/exportImage';

const MAX_AUTOSAVE_INTERVAL = 1000;
const MAX_RETRY_SAVE_TIMEOUT = 600; // 600 seconds = 10 minutes

type SaveState = 'idle' | 'error' | 'busy';
type ITimeTraveller = Instance<typeof TimeTraveller>;

/**
 * Hand-declared instance interface for ModelVersion. It sits on the projects
 * recursion boundary (`model`/`project` reach up via `getParent`, `content` holds
 * a Model), so per the MST typing pattern (§3) the interface is declared by hand
 * and not used to rebind `self` in views here — the views block already reads
 * cleanly off the raw `self`, but the interface is needed so siblings (project,
 * model_information) can reference a ModelVersion lazily.
 */
export interface IModelVersion {
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly content: IModel | null;
  readonly history: ITimeTraveller | null;
  readonly saveState: SaveState;
  readonly updated: DateTime;
  readonly created: DateTime;
  readonly contentAvailable: boolean;
  readonly model: IModelInformation;
  readonly project: IProject;
  readonly canUndo: boolean | null;
  readonly canRedo: boolean | null;
  loadContent(): Promise<void>;
  saveContent(contentSnapshot?: unknown): Promise<boolean>;
  autosave(contentSnapshot?: unknown): Promise<void>;
  startContentAutosave(): void;
  stopContentAutosave(): void;
  retrySave(): Promise<boolean>;
  triggerSave(): void;
  undo(): void | false;
  redo(): void | false;
  export(): Promise<Blob>;
}

/**
 * A 'ModelVersion' (see ARCHITECTURE.md)
 *
 * Two interesting features are implemented here:
 * - autosave
 * - undo/redo
 *
 * The basic autosave is implemented by attaching a (throttled) onSnapshot
 * listener to the content. That handler save a snapshot of the content to the
 * backend. Then there is some code around that to automatically retry on
 * failure.
 *
 * Undo/redo is implemented with the generic TimeTraveller middleware of MST.
 */
const ModelVersion = t
  .model('ModelVersion', {
    id: t.string,
    name: t.string,
    active: t.boolean,
    createdAt: t.string,
    updatedAt: t.string,
    // The content: this is the actual MERODE model. See '../model' for the
    // definition of the MST model for it.
    content: t.maybeNull(Model),
    // The TimeTraveller middleware stores its state here.
    history: t.maybeNull(TimeTraveller),
    // Are we saving or not? Or did it error?
    saveState: t.optional(t.enumeration(['idle', 'error', 'busy']), 'idle'),
  })
  .views((self0) => {
    // Rebind `self` to the hand-declared interface so the sibling view `project`
    // can read `self.model` (§4). `getParent` still receives the real node
    // `self0`.
    const self = self0 as unknown as IModelVersion;
    return {
      // a luxon DateTime of the updatedAt property
      get updated() {
        return DateTime.fromISO(self.updatedAt);
      },
      // a luxon DateTime of the createdAt property
      get created() {
        return DateTime.fromISO(self.createdAt);
      },
      // is the content loaded?
      get contentAvailable() {
        return self.content !== null;
      },
      // convenience method to get the model information (i.e. our parent)
      get model(): IModelInformation {
        // 2 levels up because 1 level up in the tree is the array of versions
        return getParent<IModelInformation>(self0, 2);
      },
      // convenience method to get the project
      get project() {
        return self.model.project;
      },
      // is there something to undo?
      get canUndo() {
        return self.history && self.history.canUndo;
      },
      // is there something to redo?
      get canRedo() {
        return self.history && self.history.canRedo;
      },
    };
  })
  .actions((self0) => {
    // This actions block calls sibling actions/views through the captured
    // closure (`self.autosave`, `self.saveContent`, `self.project`, …), so rebind
    // `self` to the hand-declared interface (§4). Same observable node at
    // runtime; `getEnv`/`getSnapshot`/`onSnapshot` still receive the real node.
    const self = self0 as unknown as IModelVersion;
    // Some state and helper functions that are only available to the actions.

    // store the function that allows to dispose our 'onSnapshot' listener
    let disposeContentAutosaver: IDisposer | null = null;
    // store the id of the setTimeout timer for automatically retry saving so
    // that we can cancel it
    let retrySaveTimer: ReturnType<typeof setTimeout> | null = null;
    // the next timeout for the retry saving setTimeout: we use an exponential
    // backoff, maxed out at MAX_RETRY_SAVE_TIMEOUT
    let retrySaveTimeout = 2;

    /**
     * Schedule a setTimeout timer to retry saving.
     *
     * Takes care of:
     * - canceling the previous timer if necessary
     * - updating the timeout interval for the next timer
     */
    function scheduleRetrySave() {
      if (retrySaveTimer != null) {
        clearTimeout(retrySaveTimer);
      }
      retrySaveTimer = setTimeout(self.autosave, retrySaveTimeout * 1000);
      retrySaveTimeout = retrySaveTimeout * 2;
      if (retrySaveTimeout > MAX_RETRY_SAVE_TIMEOUT) {
        retrySaveTimeout = MAX_RETRY_SAVE_TIMEOUT;
      }
    }

    /**
     * Cancel the timer to retry saving.
     *
     * Also reset the timeout to its initial value.
     */
    function cancelRetrySave() {
      if (retrySaveTimer !== null) {
        clearTimeout(retrySaveTimer);
        retrySaveTimer = null;
        retrySaveTimeout = 2;
      }
    }

    // The actual actions are below

    return {
      // Load the content from the backend and initialize the TimeTraveller for
      // undo/redo if it isn't there yet
      loadContent: flow(function* loadContent() {
        const api = getEnv<IStoreEnv>(self.project).api;
        const json: { content: unknown } = yield api.projects.showModelVersion(
          self.project.id,
          self.model.id,
          self.id
        );
        // MST accepts a snapshot for a `content` model assignment at runtime; the
        // instance write type doesn't include the snapshot shape, so cast at this
        // assignment boundary (documented MST gap). Writes go through the raw node
        // `self0` whose props are mutable.
        self0.content = json.content as unknown as typeof self0.content;
        if (self.history === null) {
          // `{ targetPath }` is a TimeTraveller snapshot; MST hydrates it into a
          // TimeTraveller instance. Same snapshot-write gap as `content` above.
          self0.history = { targetPath: '../content' } as unknown as typeof self0.history;
        }
      }),
      /**
       * Save the (given) content to the backend.
       *
       * If no snapshot of the content is given, it will get a snapshot.
       *
       * It also accepts the snapshot to be passed in because it is also used by
       * our autosave action, which is a (throttled) 'onSnapshot' listener that
       * gets a snapshot from MST. If we already have a snapshot, there is no
       * need to get another one.s
       */
      saveContent: flow(function* save(contentSnapshot?: unknown) {
        self0.saveState = 'busy';
        try {
          const api = getEnv<IStoreEnv>(self.project).api;
          const snapshot = contentSnapshot || getSnapshot(self.content!);
          const json: { updatedAt: string } = yield api.projects.updateModelVersion(
            self.project.id,
            self.model.id,
            self.id,
            { content: snapshot }
          );
          self0.updatedAt = json.updatedAt;
          self0.saveState = 'idle';
          // We saved successfully: cancel retry timer if necessary
          cancelRetrySave();
          return true;
        } catch (error) {
          self0.saveState = 'error';
          return false;
        }
      }),
      /**
       * Autosave. This is our onSnapshot handler that receives a snapshot from
       * MST each time the content changes and save that snapshot to the
       * backend.
       *
       * @param {*} contentSnapshot The snapshot passed in by MST
       */
      autosave(contentSnapshot?: unknown) {
        return self.saveContent(contentSnapshot).then((saved) => {
          if (!saved) {
            // not saved, schedule a retry
            scheduleRetrySave();
          }
        });
      },
      /**
       * Attach our autosave 'onSnapshot' listener to the content MST node (if
       * it isn't attached already).
       *
       * Note that we don't attach the autosave action directly, but wrap it
       * with throttle to limit the autosaving to maximum once every
       * MAX_AUTOSAVE_INTERVAL.
       */
      startContentAutosave() {
        if (disposeContentAutosaver === null) {
          disposeContentAutosaver = onSnapshot(
            self.content!,
            throttle(self.autosave, MAX_AUTOSAVE_INTERVAL)
          );
        } // else: we are already autosaving
      },
      /**
       * Dispose the autosave 'onSnapshot' listener.
       */
      stopContentAutosave() {
        if (disposeContentAutosaver !== null) {
          disposeContentAutosaver();
          disposeContentAutosaver = null;
        }
      },
      /**
       * Manually retry saving.
       */
      retrySave() {
        if (self.saveState === 'error') {
          return self.saveContent();
        } else {
          return Promise.resolve(true);
        }
      },
      /**
       * Trigger a save
       */
      triggerSave() {
        self.autosave(getSnapshot(self.content!));
      },
      // Go back one entry in the history
      undo() {
        return self.canUndo && self.history!.undo();
      },
      // Go forwared one entry in the history
      redo() {
        return self.canRedo && self.history!.redo();
      },
      // Export the version to MXP. Returns a blob of the MXP-file.
      export: flow(function* exportModelVersion() {
        const api = getEnv<IStoreEnv>(self.project).api;
        const images: Array<{ name: string; blob: Blob }> = [];
        const { bbox: edgCoordinates, image: edgImage } = yield exportEDGImage(
          self.content,
          self.model.notation
        );
        images.push({
          name: `EDG_${edgCoordinates.x.toFixed(1)}x${edgCoordinates.y.toFixed(1)}.png`,
          blob: edgImage,
        });
        for (const fsm of self.content!.uimodel.fsms) {
          // Skip orphan FSM views: `modelReference` resolves a dead fsm id to
          // `undefined` (refHelper.js), so a view left behind by an out-of-band
          // FSM/object deletion would throw on `fsm.fsm.codegeneration` and abort
          // the whole MXP export. Same dangling-reference guard family as
          // UIModel.findFSMById and the OET orphan-row guards.
          if (fsm.fsm && fsm.fsm.codegeneration) {
            const { bbox: coords, image: fsmImage } = yield exportFSMImage(self.content, fsm.fsm);
            const typeSuffix = fsm.fsm.type === 'DEFAULT' ? 'Default' : 'User_Specified';
            const name = `FSM_${fsm.fsm.objectType.name.toUpperCase()}_${typeSuffix}_${coords.x.toFixed(
              1
            )}x${coords.y.toFixed(1)}.png`;
            images.push({
              name,
              blob: fsmImage,
            });
          }
        }
        const blob = yield api.projects.exportModelVersion(
          self.project.id,
          self.model.id,
          self.id,
          images
        );
        return blob;
      }),
    };
  });

export default ModelVersion;
